import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Award,
  CalendarDays,
  CirclePlus,
  FlagTriangleRight,
  IdCard,
  Landmark,
  UserCog,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, cn } from '@racing/ui';

interface NavItem {
  label: string;
  icon: ReactNode;
  to: string;
  /** Only match this route exactly; otherwise a prefix match highlights it. */
  end?: boolean;
}

/**
 * The navigation list, shared by the desktop rail and the mobile sheet so the
 * two can never drift apart.
 *
 * Icons are chosen for the drag-racing domain rather than MUI's generic set:
 * a flag for race results, a landmark for the track venue, an accreditation
 * award for sanctioning bodies.
 */
const SidebarNav = ({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}) => {
  const { isAdmin, isTrackOperator } = useAuth();
  const { pathname } = useLocation();
  const canCreateEvents = isAdmin || isTrackOperator;
  const canEditRaceData = isAdmin || isTrackOperator;

  const mainItems: NavItem[] = [
    { label: 'Events', icon: <CalendarDays />, to: '/events', end: true },
    ...(canCreateEvents
      ? [{ label: 'New Event', icon: <CirclePlus />, to: '/events/new' }]
      : []),
    ...(canEditRaceData
      ? [{ label: 'Race Data', icon: <FlagTriangleRight />, to: '/race-data' }]
      : []),
  ];

  const adminItems: NavItem[] = [
    { label: 'Tracks', icon: <Landmark />, to: '/admin/tracks' },
    { label: 'Sanctioning Bodies', icon: <Award />, to: '/admin/sanctioning-bodies' },
    { label: 'Managed Users', icon: <UserCog />, to: '/admin/users' },
    { label: 'App Users', icon: <IdCard />, to: '/admin/app-users' },
  ];

  /**
   * Active state is computed here rather than taken from NavLink's render-prop
   * `className`. When collapsed, each item is wrapped in a Radix Tooltip with
   * `asChild`, and Slot cannot merge a *function* className onto the child —
   * the active styling silently disappeared in the collapsed rail.
   */
  const isItemActive = (item: NavItem) =>
    item.end ? pathname === item.to : pathname.startsWith(item.to);

  const renderItem = (item: NavItem) => {
    const active = isItemActive(item);
    return (
      <Tooltip key={item.to} label={collapsed ? item.label : ''} side="right">
        <Link
          to={item.to}
          onClick={onNavigate}
          aria-current={active ? 'page' : undefined}
          className={cn(
            // 44px is the comfortable touch target on a tablet at the track.
            'flex min-h-11 items-center gap-3 rounded-md px-3 py-2 outline-none transition-colors',
            'font-sans text-base text-ink',
            'hover:bg-surface-sunk focus-visible:ring-2 focus-visible:ring-brand',
            collapsed && 'justify-center px-2',
            active && 'bg-brand/10 font-semibold text-brand-deep hover:bg-brand/16'
          )}
        >
          <span className="grid shrink-0 place-items-center [&_svg]:size-5">{item.icon}</span>
          {!collapsed && <span className="truncate">{item.label}</span>}
        </Link>
      </Tooltip>
    );
  };

  return (
    <nav aria-label="Main" className="flex flex-col gap-1 p-2">
      {mainItems.map(renderItem)}

      {isAdmin && (
        <>
          <hr className="my-2 border-0 border-t border-line" />
          {!collapsed && (
            <h2 className="px-3 pb-1 font-sans text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-ink-faint">
              Administration
            </h2>
          )}
          {adminItems.map(renderItem)}
        </>
      )}
    </nav>
  );
};

export default SidebarNav;
